import styled from '@emotion/styled'
import { MotionStyle } from 'framer-motion'
import { Component } from 'react'
import { ConfigurationDetail, IdentifierType } from 'stylewhere/api'
import { Box, Button, Icons, Input, ItemIdentifierInfoModal, Text } from 'stylewhere/components'
import { T, __ } from 'stylewhere/shared/i18n'
import { closeModal, openModal } from 'stylewhere/shared/utils'

interface Props {
  marginBottom?: number
  widthPerc?: number
  inLine?: boolean
  identifier: ConfigurationDetail
  style?: MotionStyle
  buttons?: { text: string; onClick: () => void }[]
  onInputSubmit?: (value: string) => void
  layout?: any
  isAssociation?: boolean
  identifiersCount?: number
  labelsByType?: Partial<Record<IdentifierType, string>>
  hideCode?: boolean
  hideStatusLabel?: boolean
  renderSimpleItemIdentifierAsNormalIdentifier?: boolean
  showIdentifierDetail?: boolean
}

const LvUhfTagIcon = (props: any) => <Icons.Rfid {...props} />
const LvNfcTagIcon = (props: any) => <Icons.Nfc {...props} />
const LvSimpleItemIcon = (props: any) => <Icons.Serial {...props} />
const LvAssociationIcon = (props: any) =>
  props.status === 'ERROR' ? <Icons.AssociationError {...props} /> : <Icons.Encoding {...props} />

const lvIdentifierTypes = {
  UHF_TAG: { icon: LvUhfTagIcon, text: 'TAG UHF' },
  NFC_TAG: { icon: LvNfcTagIcon, text: 'TAG NFC' },
  SIMPLE_ITEM_IDENTIFIER: { icon: LvSimpleItemIcon, text: 'IDENTIFIER' },
  ASSOCIATION: { icon: LvAssociationIcon, get text() { return __(T.misc.association) } }
}

export class IdentifierBox extends Component<Props> {
  textColor = {
    ERROR: 'white',
    NFC_COLLISION: 'white',
    DEFAULT: 'black'
  }

  status = {
    CONFIRMED: __(T.misc.confirmed),
    ERROR: __(T.error.error),
    TO_BE_READ: __(T.misc.reading) + '...',
    PROCESSING: __(T.misc.processing) + '...',
    IGNORED: __(T.misc.ignored),
    SKIPPED: __(T.misc.skipped),
    READY: __(T.misc.ready_to_read),
    EXPECTED: __(T.misc.expected)
  }

  private resolveWidthPerc(isSimpleItemIdentifier: boolean): number {
    const { layout, inLine, widthPerc, isAssociation, identifiersCount } = this.props
    let result = layout?.widthPerc ?? (isSimpleItemIdentifier && !inLine ? 100 : widthPerc || 100)
    if (isAssociation && layout && identifiersCount) {
      result = inLine ? 100 : identifiersCount === 1 || identifiersCount % 2 === 0 ? 100 : 49
    }
    return result
  }

  private resolveTextColor(identifier: ConfigurationDetail): string {
    const color = this.textColor[identifier._status as any] ?? this.textColor.DEFAULT
    if (
      identifier.identifierType === 'ASSOCIATION' &&
      (identifier._status === 'ERROR' || identifier._status === 'NFC_COLLISION')
    )
      return this.textColor.DEFAULT
    return color
  }

  private resolveIconStyle(layout: any, isFullWidth: boolean) {
    const iconSize = layout?.iconSize ?? (isFullWidth ? 80 : 60)
    return {
      width: iconSize,
      height: iconSize,
      marginBottom: layout?.iconMarginBottom ?? (isFullWidth ? 0 : 10),
      marginRight: layout?.iconMarginRight ?? (isFullWidth ? 30 : 0)
    }
  }

  private renderContent(
    isSimpleItemIdentifier: boolean,
    textColor: string,
    label: string,
    idfTypeText: string | undefined,
    isFullWidth: boolean
  ) {
    const {
      identifier,
      layout,
      hideCode,
      hideStatusLabel,
      renderSimpleItemIdentifierAsNormalIdentifier,
      onInputSubmit,
      isAssociation,
      showIdentifierDetail
    } = this.props
    return (
      <Box row center>
        <Box flex>
          {(!isSimpleItemIdentifier || renderSimpleItemIdentifierAsNormalIdentifier) && (
            <>
              <Text
                data-testid={`identifier-role-${identifier.role}`}
                bold
                fontSize={layout?.fontSizeTagType ?? 24}
                style={{ marginBottom: isFullWidth ? 5 : 0, color: textColor }}
              >
                {label}
              </Text>
              {identifier.code && !hideCode && (
                <Text
                  data-testid={`identifier-code-${identifier.code}`}
                  fontSize={layout?.fontSizeCode ?? 16}
                  bold
                  style={{ color: textColor }}
                >
                  {identifier.code}
                </Text>
              )}
              {!hideStatusLabel && (
                <Text
                  bold
                  data-testid={`identifier-status-${identifier._status}`}
                  fontSize={layout?.fontSizeStatus ?? 20}
                  style={{ color: textColor }}
                >
                  {isAssociation && identifier._status === 'READY'
                    ? __(T.misc.start_and_read)
                    : this.status[identifier._status ?? 'TO_BE_READ']}
                </Text>
              )}
            </>
          )}
          {!!isSimpleItemIdentifier && !renderSimpleItemIdentifierAsNormalIdentifier && onInputSubmit && (
            <Input
              testId={`identifier-role-${identifier.role}`}
              disabled={(identifier._status ?? 'TO_BE_READ') !== 'TO_BE_READ'}
              startFocus
              defaultValue={identifier.code}
              onEnter={onInputSubmit}
              label={identifier.role || idfTypeText}
            />
          )}
          {identifier._error && (
            <Text fontSize={14} style={{ marginTop: 5, color: textColor }}>
              {identifier._error}
            </Text>
          )}
        </Box>
        {identifier.identifierId && showIdentifierDetail && (
          <Box>
            <Icons.Info style={{ color: identifier._error ? '#FFF' : '#000' }} />
          </Box>
        )}
      </Box>
    )
  }

  private renderButtons() {
    const { buttons } = this.props
    if (!buttons) return null
    return (
      <Box center>
        {buttons.map((button, index) => (
          <IdentifierButton key={'identifier-button-' + index} title={button.text} onClick={button.onClick} />
        ))}
      </Box>
    )
  }

  private readonly openDetail = () => {
    const { identifier } = this.props
    return openModal({
      id: 'item-identifier-modal',
      closeOnOverlayClick: true,
      modal: (
        <ItemIdentifierInfoModal visible identifier={identifier} onClose={() => closeModal('item-identifier-modal')} />
      )
    })
  }

  render() {
    const { marginBottom = 16, identifier, style, layout, inLine, labelsByType, showIdentifierDetail } = this.props

    const isSimpleItemIdentifier =
      identifier.identifierType === 'SIMPLE_ITEM_IDENTIFIER' && identifier._status !== 'CONFIRMED'

    const widthPerc = this.resolveWidthPerc(isSimpleItemIdentifier)
    const isFullWidth = inLine || widthPerc === 100
    const iconStyle = this.resolveIconStyle(layout, isFullWidth)
    const textColor = this.resolveTextColor(identifier)
    const idfType = lvIdentifierTypes[identifier.identifierType]
    const roleText = identifier.role ? ` (${identifier.role})` : ''
    const label = labelsByType?.[identifier.identifierType] ?? `${idfType?.text}${roleText}`

    return (
      <IdentifierContainer
        data-testid="identifier-box"
        status={identifier._status}
        identifierType={identifier.identifierType}
        style={style}
        pv={layout?.paddingVertical ?? 10}
        ph={layout?.paddingHorizontal ?? 30}
        row
        widthPerc={widthPerc}
        mb={marginBottom}
        minHeight={layout?.minHeight ?? '175px'}
        onClick={identifier.identifierId && showIdentifierDetail ? this.openDetail : undefined}
      >
        <Box vcenter flex row={layout?.row || isFullWidth}>
          {idfType?.icon({ style: iconStyle, fill: textColor, status: identifier._status })}
          <Box flex={1}>{this.renderContent(isSimpleItemIdentifier, textColor, label, idfType?.text, isFullWidth)}</Box>
        </Box>
        {this.renderButtons()}
      </IdentifierContainer>
    )
  }
}

const IdentifierContainer = styled(Box)<{
  widthPerc: number
  status?:
    | 'CONFIRMED'
    | 'ERROR'
    | 'TO_BE_READ'
    | 'PROCESSING'
    | 'IGNORED'
    | 'SKIPPED'
    | 'WRITING'
    | 'NFC_COLLISION'
    | 'READY'
    | 'EXPECTED'
  identifierType?: IdentifierType
  minHeight?: string
}>`
  background-color: white;
  border-radius: 15px;
  min-height: ${({ minHeight }) => minHeight || '175px'};
  max-height: 40vh;
  align-items: center;
  box-shadow:
    0px 1px 4px rgb(0 0 0 / 15%),
    inset 0px 0px 7px rgba(0, 0, 0, 0.1);
  width: ${({ widthPerc = 100 }) => (widthPerc === 100 ? `100%;` : `calc(${widthPerc}% - 4px);`)};
  ${({ status }) => status === 'ERROR' && `background-color: #EB5757;`}
  ${({ status }) => status === 'NFC_COLLISION' && `background-color: #EB5757;`}
  ${({ status }) => status === 'CONFIRMED' && `background-color: #75EBA8;`}
  ${({ status }) => status === 'IGNORED' && `background-color: #F2F2F2;`}
  ${({ status }) => status === 'SKIPPED' && `background-color: #F2F2F2;`}
  ${({ status }) => status === 'PROCESSING' && `box-shadow:inset 0 0px 10px rgb(117 235 168 / 70%);`}
  ${({ status, identifierType }) =>
    status === 'ERROR' && identifierType === 'ASSOCIATION' && `background-color: #E0E0E0;`}
     ${({ status = 'TO_BE_READ', identifierType }) =>
    status === 'TO_BE_READ' && identifierType === 'SIMPLE_ITEM_IDENTIFIER' && `background-color: #E0E0E0;`}
`

const IdentifierButton = styled((props) => <Button {...props} />)`
  background: #333333;
  color: white;
  border-radius: 100px;
  flex: 0;
  width: fit-content;
  min-width: 200px;
  padding: 0px 50px;
`
